// RealityAI Agent UI — AgentRunView (PHASE K7).
//
// Observes a run through the K6 SSE endpoint (client-side consumer):
//   fetch streaming + Bearer + Last-Event-ID reconnect + seq dedup.
//
// Lifecycle (K7 §13): the view renders the persisted run state; the SSE
// terminal event (run.completed / run.failed / run.cancelled) is the
// AUTHORITATIVE lifecycle update — the cancel response alone is never
// trusted (K6 known limitation #3: queued cancel race).
//
// Rendered stream: plan, steps, model events, tool events, message deltas,
// errors, terminal state. Event data is treated as data only (no innerHTML,
// no eval — everything renders through React text nodes).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Square, RefreshCw, AlertTriangle, WifiOff } from "lucide-react";
import {
  Agent,
  AgentRun,
  fetchAgentRun,
  cancelAgentRun,
  isTerminalStatus,
  agentErrorInfo,
} from "@/lib/api/agents";
import { AgentSseClient } from "@/lib/agents/sse";
import {
  AgentEvent,
  TERMINAL_EVENTS,
  terminalEventStatus,
  eventSummary,
  eventNameLabel,
  runStatusLabel,
  formatTime,
} from "./types";
import { cn } from "@/lib/utils";

interface AgentRunViewProps {
  agent: Agent;
  initialRun: AgentRun;
  onClose: () => void;
}

type ConnState = "connecting" | "live" | "reconnecting" | "closed" | "error";

export function AgentRunView({ agent, initialRun, onClose }: AgentRunViewProps) {
  const [run, setRun] = useState<AgentRun>(initialRun);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [conn, setConn] = useState<ConnState>("connecting");
  const [connError, setConnError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNote, setCancelNote] = useState<string | null>(null);
  const clientRef = useRef<AgentSseClient | null>(null);
  const runRef = useRef(run);
  runRef.current = run;

  // Terminal state: SSE terminal event is authoritative; the persisted
  // status from the REST read is the fallback (history open).
  const terminal = isTerminalStatus(run.status);

  const handleEvent = useCallback((ev: AgentEvent) => {
    setEvents((prev) => [...prev, ev]);
    // Apply authoritative lifecycle transitions.
    if (TERMINAL_EVENTS.has(ev.event_name)) {
      const status = terminalEventStatus(ev.event_name);
      if (status) {
        setRun((r) => ({
          ...r,
          status,
          result: typeof ev.data.result === "string" ? (ev.data.result as string) : r.result,
          error:
            ev.event_name === "run.failed"
              ? ({
                  code:
                    (ev.data.error as Record<string, unknown> | undefined)?.code ??
                    (ev.data.code as string | undefined),
                  message:
                    ((ev.data.error as Record<string, unknown> | undefined)?.message as string | undefined) ??
                    (ev.data.message as string | undefined),
                } as AgentRun["error"])
              : r.error,
        }));
      }
    }
  }, []);

  // Connect the SSE client once (replay covers events written since start;
  // Last-Event-ID is empty for a fresh client).
  useEffect(() => {
    const client = new AgentSseClient({
      runId: initialRun.id,
      callbacks: {
        onEvent: handleEvent,
        onTerminal: (ev) => {
          handleEvent(ev);
          // Terminal replay → close: the client stops reconnecting on its own.
        },
        onError: (err) => {
          setConnError(err.message);
        },
        onReconnecting: () => {
          setConn("reconnecting");
        },
        onConnected: () => {
          setConn("live");
          setConnError(null);
        },
        onClose: (reason) => {
          if (reason === "aborted") return;
          setConn(reason === "terminal" ? "closed" : "error");
          // Refresh the persisted state once (the REST read is the final
          // source for terminal fields like result/duration).
          void refreshRun(initialRun.id);
        },
      },
    });
    clientRef.current = client;
    client.start();

    // If the run is already terminal (opened from history), we still do a
    // full replay (server replays from the beginning → terminal → close) so
    // the user sees the whole history of the run.
    if (isTerminalStatus(initialRun.status)) {
      setConn("live");
    }

    return () => {
      client.abort();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRun.id]);

  const refreshRun = useCallback(async (runId: string) => {
    try {
      const fresh = await fetchAgentRun(runId);
      setRun((r) => ({ ...fresh, result: fresh.result ?? r.result, error: fresh.error ?? r.error }));
    } catch {
      /* keep the current view state */
    }
  }, []);

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    setCancelNote(null);
    try {
      await cancelAgentRun(runRef.current.id);
      // Do NOT switch to terminal here — the SSE run.cancelled event is the
      // authoritative transition (queued cancel race, K6 limitation #3).
      setCancelNote("Запрошена отмена — ожидание подтверждения от выполнения…");
    } catch (e) {
      const info = agentErrorInfo(e);
      if (info.status === 409) {
        setCancelNote("Запуск уже завершён");
      } else if (info.status === 404) {
        setCancelNote("Запуск не найден");
      } else {
        setCancelNote(info.detail || "Не удалось отменить запуск");
      }
    } finally {
      setCancelling(false);
    }
  }, []);

  // Derived: plan + steps for the summary rail (K3 payloads: {node, step_index}).
  const planSteps = useMemo(() => {
    const steps: { title: string; state: "started" | "completed" }[] = [];
    for (const ev of events) {
      if (ev.event_name !== "step.started" && ev.event_name !== "step.completed") continue;
      const node = typeof ev.data.node === "string" ? ev.data.node : "";
      const idx = ev.data.step_index != null ? ` #${ev.data.step_index}` : "";
      const title = node ? `${node}${idx}` : `Шаг${idx}`;
      if (ev.event_name === "step.started") {
        // A step re-starts (e.g. loop) — refresh the matching open step.
        const open = [...steps].reverse().find((s2) => s2.state === "started" && s2.title === title);
        if (!open) steps.push({ title, state: "started" });
      } else {
        const open = [...steps].reverse().find((s2) => s2.state === "started" && s2.title === title);
        if (open) open.state = "completed";
        else steps.push({ title, state: "completed" });
      }
    }
    return steps;
  }, [events]);

  // Auto-scroll the event log to the newest entry.
  const logRef = useRef<HTMLDivElement | null>(null);
  const followRef = useRef(true);
  useEffect(() => {
    if (followRef.current && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events]);

  const connBadge = useMemo(() => {
    switch (conn) {
      case "live":
        return { text: "Соединение активно", cls: "text-emerald-400" };
      case "reconnecting":
        return { text: "Переподключение…", cls: "text-amber-400" };
      case "connecting":
        return { text: "Подключение…", cls: "text-secondary" };
      case "closed":
        return { text: "Поток завершён", cls: "text-tertiary" };
      case "error":
        return { text: "Нет соединения", cls: "text-red-400" };
    }
  }, [conn]);

  return (
    <div className="absolute inset-0 z-[90] bg-app flex flex-col min-w-0">
      {/* Header — fixed */}
      <div className="shrink-0 flex items-center gap-3 px-4 md:px-6 h-14 border-b border-hairline">
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
          aria-label="Назад"
        >
          <X size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-primary truncate">
              {run.agent_name || agent.name}
            </h2>
            <span className="text-xs text-secondary shrink-0">{runStatusLabel(run.status)}</span>
          </div>
          <div className="text-[11px] text-tertiary truncate">{run.task}</div>
        </div>
        <span className={cn("text-[11px] flex items-center gap-1.5 shrink-0", connBadge.cls)}>
          {conn === "error" ? <WifiOff size={12} /> : conn === "reconnecting" ? <RefreshCw size={12} className="animate-spin" /> : null}
          {connBadge.text}
        </span>
        {!terminal && (
          <button
            onClick={() => void handleCancel()}
            disabled={cancelling}
            className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
          >
            <Square size={13} /> {cancelling ? "Отмена…" : "Остановить"}
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Event log — the only scroll zone */}
        <div
          ref={logRef}
          onScroll={() => {
            const el = logRef.current;
            if (!el) return;
            followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          }}
          className="flex-1 min-w-0 overflow-y-auto"
        >
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-5 space-y-2">
            {events.length === 0 && (
              <div className="py-10 text-center text-sm text-tertiary">
                {terminal
                  ? "События не записаны"
                  : "Ожидание событий… Запуск выполняется, события появятся здесь."}
              </div>
            )}
            {events.map((ev) => (
              <EventRow key={`${ev.run_id}-${ev.seq}`} ev={ev} />
            ))}
            {cancelNote && (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertTriangle size={14} /> {cancelNote}
              </div>
            )}
            {connError && conn === "reconnecting" && (
              <div className="text-[11px] text-tertiary">
                Последнее событие: seq {events.length ? events[events.length - 1].seq : "—"}
              </div>
            )}
          </div>
        </div>

        {/* Summary rail — plan / steps / terminal (fixed, no scroll) */}
        <div className="hidden lg:flex w-72 shrink-0 border-l border-hairline flex-col min-h-0">
          <div className="px-4 pt-4 pb-2 text-[11px] font-semibold text-tertiary uppercase tracking-wider shrink-0">
            План
          </div>
          <div className="px-4 pb-4 space-y-1.5 overflow-y-auto min-h-0">
            {planSteps.length === 0 && (
              <div className="text-sm text-tertiary">Шаги появятся при запуске</div>
            )}
            {planSteps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    "w-2 h-2 rounded-full shrink-0",
                    s.state === "completed" ? "bg-emerald-400" : "bg-accent animate-pulse"
                  )}
                />
                <span className={cn("truncate", s.state === "completed" ? "text-secondary" : "text-primary")}>
                  {s.title}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-auto px-4 pb-5 space-y-2 border-t border-hairline pt-4 shrink-0">
            {run.result && (
              <div>
                <div className="text-[11px] font-semibold text-tertiary uppercase tracking-wider mb-1">
                  Результат
                </div>
                <p className="text-sm text-primary whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                  {run.result}
                </p>
              </div>
            )}
            {run.error && (
              <div>
                <div className="text-[11px] font-semibold text-tertiary uppercase tracking-wider mb-1">
                  Ошибка
                </div>
                <p className="text-sm text-red-400 break-words">{run.error.message || run.error.code || "—"}</p>
              </div>
            )}
            {run.duration_seconds != null && (
              <div className="text-[11px] text-tertiary">
                Длительность: {formatDuration(run.duration_seconds)}
              </div>
            )}
            {run.started_at && (
              <div className="text-[11px] text-tertiary">
                Начало: {formatTime(run.started_at)}
                {run.finished_at ? ` · Конец: ${formatTime(run.finished_at)}` : ""}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventRow({ ev }: { ev: AgentEvent }) {
  const terminal = TERMINAL_EVENTS.has(ev.event_name);
  const isError = ev.event_name === "error" || ev.event_name === "run.failed" || ev.event_name === "limit_exceeded";
  const delta = ev.event_name === "message.delta";
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2",
        terminal
          ? ev.event_name === "run.completed"
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-red-500/30 bg-red-500/5"
          : delta
            ? "border-transparent bg-transparent"
            : "border-hairline bg-surface"
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "text-[10px] font-medium uppercase tracking-wide",
            terminal ? (ev.event_name === "run.completed" ? "text-emerald-400" : "text-red-400") : isError ? "text-red-400" : "text-accent"
          )}
        >
          {eventNameLabel(ev.event_name)}
        </span>
        <span className="text-[10px] text-tertiary">#{ev.seq}</span>
      </div>
      {(() => {
        const summary = eventSummary(ev);
        return summary ? (
          <div className={cn("mt-1 text-sm break-words whitespace-pre-wrap", delta ? "text-primary" : "text-secondary")}>
            {summary}
          </div>
        ) : null;
      })()}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} с`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m} мин ${s} с` : `${m} мин`;
}
